import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import useAppStore from '../store';
import { shouldBypassAutoBackup } from '../utils/backupBypass';
import {
    clearSavedBackupFolder,
    getSavedBackupFolderName,
    isClientPcSafeDue,
    markClientPcSafeDone,
    pickBackupDirectory,
    saveBackupJson,
    supportsBackupFolderPicker,
} from '../utils/backupFolder';

const ShieldAlertIcon = ({ size = 24, className = '' }: { size?: number; className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
);

export type ClientPcSafeReason = 'manual' | 'interval' | 'logout';

export function buildClientBackupExport(clientData: any) {
    return {
        metadata: {
            exportDate: new Date().toISOString(),
            type: 'client_backup',
            appVersion: '1.4',
        },
        clientData,
    };
}

export function buildClientBackupFileName(clientData: any): string {
    const dateStr = new Date().toISOString().split('T')[0];
    const name = clientData?.salonname || clientData?.firmenname || 'Mandant';
    const safeName = String(name).replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'mandant';
    return `PreisExpert_Backup_${safeName}_${dateStr}.json`;
}

/** Speichert in festen PC-Safe-Ordner (falls gesetzt) oder als Download. */
export async function runClientPcSafeBackup(clientData: any): Promise<'folder' | 'download'> {
    const exportData = buildClientBackupExport(clientData);
    const fileName = buildClientBackupFileName(clientData);
    const mode = await saveBackupJson(fileName, exportData, 'client');
    markClientPcSafeDone();
    return mode;
}

interface ClientPcSafeManagerProps {
    showNotification: (msg: string, type: 'success' | 'error') => void;
    /** true = 24h-Reminder aktiv (PWA / Mandant bleibt eingeloggt) */
    enabled?: boolean;
    /** PC-Safe-Button im Header → gleiches Modal wie 24h */
    manualOpen?: boolean;
    onManualClose?: () => void;
    /** Logout-Flow */
    logoutPromptOpen?: boolean;
    onLogoutContinue?: () => void;
    onLogoutCancel?: () => void;
}

const ClientPcSafeManager: React.FC<ClientPcSafeManagerProps> = ({
    showNotification,
    enabled = true,
    manualOpen = false,
    onManualClose,
    logoutPromptOpen = false,
    onLogoutContinue,
    onLogoutCancel,
}) => {
    const [showIntervalModal, setShowIntervalModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [folderName, setFolderName] = useState(() => getSavedBackupFolderName('client'));
    const canPickFolder = supportsBackupFolderPicker();
    const hasFolder = Boolean(folderName);

    const refreshFolderName = () => setFolderName(getSavedBackupFolderName('client'));

    useEffect(() => {
        if (manualOpen || logoutPromptOpen || showIntervalModal) {
            refreshFolderName();
        }
    }, [manualOpen, logoutPromptOpen, showIntervalModal]);

    useEffect(() => {
        if (!enabled) return;
        if (shouldBypassAutoBackup()) return;
        if (logoutPromptOpen || manualOpen) return;

        const check = () => {
            if (shouldBypassAutoBackup()) return;
            if (isClientPcSafeDue()) setShowIntervalModal(true);
        };

        check();
        const id = window.setInterval(check, 15 * 60 * 1000);
        return () => window.clearInterval(id);
    }, [enabled, logoutPromptOpen, manualOpen]);

    const handlePickFolder = async () => {
        try {
            const handle = await pickBackupDirectory('client');
            if (handle) {
                refreshFolderName();
                showNotification(`PC-Safe-Ordner gesetzt: ${handle.name}`, 'success');
            }
        } catch {
            showNotification('Ordner konnte nicht gesetzt werden.', 'error');
        }
    };

    const handleClearFolder = async () => {
        await clearSavedBackupFolder('client');
        refreshFolderName();
        showNotification('Speicherort entfernt — du kannst einen neuen Ordner festlegen.', 'success');
    };

    const closeManualOrInterval = () => {
        setShowIntervalModal(false);
        onManualClose?.();
    };

    const handleSave = async () => {
        // Chrome/Edge: ohne festen Ordner kein Speichern — erst entscheiden
        if (canPickFolder && !getSavedBackupFolderName('client')) {
            showNotification('Bitte zuerst einen festen Speicherort wählen.', 'error');
            return;
        }

        setIsSaving(true);
        try {
            const clientData = useAppStore.getState().clientData;
            const mode = await runClientPcSafeBackup(clientData);
            const where = mode === 'folder'
                ? `in „${getSavedBackupFolderName('client') || 'Ordner'}“`
                : 'als Download';
            showNotification(`PC-Safe gespeichert ${where}.`, 'success');
            setShowIntervalModal(false);
            onManualClose?.();
            if (logoutPromptOpen) onLogoutContinue?.();
        } catch {
            showNotification('Fehler beim Speichern auf dem PC.', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSkipInterval = () => {
        markClientPcSafeDone();
        setShowIntervalModal(false);
    };

    const reason: ClientPcSafeReason = logoutPromptOpen
        ? 'logout'
        : manualOpen
          ? 'manual'
          : 'interval';

    const modalOpen = logoutPromptOpen || manualOpen || showIntervalModal;
    if (!modalOpen) return null;

    const needsFolderFirst = canPickFolder && !hasFolder;
    const saveEnabled = !isSaving && (!canPickFolder || hasFolder);

    const eyebrow =
        reason === 'logout' ? 'Logout · PC-Safe'
            : reason === 'manual' ? 'PC-Safe · Mandanten-Akte'
                : '24h · PC-Safe';

    const bodyCopy =
        reason === 'logout' ? (
            <>
                Bevor du dich abmeldest: Lege einmal einen <strong className="text-emerald-300">festen Speicherort</strong> fest
                (wie beim Admin-Backup) und speichere deine Akte dort. Danach reicht jedes Mal ein Klick auf „Sichern“.
                Ohne Sicherung kannst du trotzdem ausloggen.
            </>
        ) : reason === 'manual' ? (
            <>
                <strong className="text-emerald-300">Schritt 1:</strong> Wähle einen Ordner auf deinem PC — dort landen
                alle künftigen Sicherungen automatisch (nach Bestätigung).
                <br />
                <strong className="text-emerald-300">Schritt 2:</strong> Speichern bestätigen. Den Ordner kannst du
                jederzeit ändern oder entfernen.
            </>
        ) : (
            <>
                Dein letztes PC-Safe ist älter als <strong className="text-emerald-300">24 Stunden</strong> (oder noch nie).
                Ideal für die installierte App (PWA), wenn du dich selten ausloggst.
                {needsFolderFirst
                    ? ' Zuerst einen festen Speicherort wählen — danach einmal speichern bestätigen.'
                    : ' Einmal speichern bestätigen — die Datei landet im festgelegten Ordner.'}
            </>
        );

    return createPortal(
        <AnimatePresence>
            {modalOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100000] bg-black/95 backdrop-blur-md flex items-center justify-center p-4"
                >
                    <motion.div
                        initial={{ scale: 0.95 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0.95 }}
                        className="bg-slate-900 border border-emerald-500/40 rounded-2xl p-8 max-w-md w-full shadow-[0_0_50px_rgba(16,185,129,0.12)] flex flex-col items-center text-center font-sans"
                    >
                        <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mb-6 shadow-inner border border-emerald-500/20">
                            <ShieldAlertIcon size={32} className="text-emerald-400" />
                        </div>
                        <div className="text-[10px] font-black uppercase tracking-[0.25em] text-emerald-400/80 mb-2">
                            {eyebrow}
                        </div>
                        <h2 className="text-emerald-300 text-2xl font-black uppercase tracking-widest mb-4">
                            {needsFolderFirst ? 'Speicherort festlegen' : 'Daten auf dem PC sichern'}
                        </h2>
                        <p className="bg-slate-800 border border-slate-700 text-slate-300 mb-4 text-sm leading-relaxed p-4 rounded-xl text-left">
                            {bodyCopy}
                        </p>

                        <div
                            className={`w-full mb-5 p-3 rounded-xl bg-black/40 text-left border ${
                                needsFolderFirst
                                    ? 'border-amber-400/50 ring-1 ring-amber-400/30'
                                    : 'border-emerald-500/25'
                            }`}
                        >
                            <div className="text-[10px] uppercase font-black tracking-widest text-emerald-300 mb-1">
                                Speicherort · PC-Safe (nur deine Akte)
                            </div>
                            {canPickFolder ? (
                                <>
                                    <p className="text-xs text-slate-400 mb-2 leading-relaxed">
                                        {hasFolder ? (
                                            <>
                                                Aktuell: <span className="text-white font-bold">{folderName}</span>
                                            </>
                                        ) : (
                                            <>
                                                Noch kein Ordner — bitte jetzt wählen. Danach speichert jede Bestätigung
                                                genau dorthin (wie Master-Backup im Admin).
                                            </>
                                        )}
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            onClick={() => { void handlePickFolder(); }}
                                            disabled={isSaving}
                                            className={`px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest border disabled:opacity-50 ${
                                                needsFolderFirst
                                                    ? 'bg-emerald-500 text-slate-950 border-emerald-400 hover:bg-emerald-400'
                                                    : 'bg-slate-800 hover:bg-slate-700 text-white border-slate-600'
                                            }`}
                                        >
                                            {hasFolder ? 'Ordner ändern' : 'Jetzt Ordner festlegen'}
                                        </button>
                                        {hasFolder && (
                                            <button
                                                type="button"
                                                onClick={() => { void handleClearFolder(); }}
                                                disabled={isSaving}
                                                className="px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-rose-300 border border-transparent hover:border-rose-500/30 disabled:opacity-50"
                                            >
                                                Ordner entfernen
                                            </button>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <p className="text-xs text-slate-500 leading-relaxed">
                                    Fester Ordner nur in Chrome/Edge (auch als PWA). Andernfalls landet die Datei im
                                    Downloads-Ordner.
                                </p>
                            )}
                        </div>

                        <button
                            type="button"
                            onClick={() => { void handleSave(); }}
                            disabled={!saveEnabled}
                            title={needsFolderFirst ? 'Zuerst Speicherort festlegen' : undefined}
                            className={`w-full font-black uppercase tracking-widest text-sm py-4 rounded-xl transition-colors shadow-lg ${
                                saveEnabled
                                    ? 'bg-emerald-500 hover:bg-emerald-400 text-slate-950'
                                    : 'bg-slate-700 text-slate-400 cursor-not-allowed'
                            }`}
                        >
                            {isSaving
                                ? 'Speichere…'
                                : needsFolderFirst
                                  ? 'Zuerst Ordner festlegen'
                                  : 'Jetzt speichern (bestätigen)'}
                        </button>

                        {reason === 'logout' && (
                            <>
                                <button
                                    type="button"
                                    disabled={isSaving}
                                    onClick={() => onLogoutContinue?.()}
                                    className="w-full mt-3 bg-transparent hover:bg-slate-800 text-slate-400 hover:text-slate-200 border border-slate-700 font-black uppercase tracking-widest text-sm py-3 rounded-xl transition-colors disabled:opacity-50"
                                >
                                    Ohne Sicherung ausloggen
                                </button>
                                <button
                                    type="button"
                                    disabled={isSaving}
                                    onClick={() => onLogoutCancel?.()}
                                    className="w-full mt-2 text-[10px] uppercase tracking-widest font-bold text-slate-500 hover:text-slate-300 py-2 disabled:opacity-50"
                                >
                                    Abbrechen (eingeloggt bleiben)
                                </button>
                            </>
                        )}

                        {reason === 'interval' && (
                            <button
                                type="button"
                                disabled={isSaving}
                                onClick={handleSkipInterval}
                                className="w-full mt-3 bg-transparent hover:bg-slate-800 text-slate-400 hover:text-slate-200 border border-slate-700 font-black uppercase tracking-widest text-sm py-3 rounded-xl transition-colors disabled:opacity-50"
                            >
                                Kein Bock — später
                            </button>
                        )}

                        {reason === 'manual' && (
                            <button
                                type="button"
                                disabled={isSaving}
                                onClick={closeManualOrInterval}
                                className="w-full mt-3 bg-transparent hover:bg-slate-800 text-slate-400 hover:text-slate-200 border border-slate-700 font-black uppercase tracking-widest text-sm py-3 rounded-xl transition-colors disabled:opacity-50"
                            >
                                Schließen
                            </button>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
};

export default ClientPcSafeManager;
